import type { FC } from 'react';
import { useState } from 'react';

import { Button } from '../../../components/ui/button';
import {
  type PhantomLogEntry,
  clearPhantomDebugLogs,
  getPhantomDebugLogs,
} from '../../../utils/phantomDebugLog/phantomDebugLog';

const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);

  return isNaN(date.getTime())
    ? '(invalid date)'
    : date.toISOString().replace('T', ' ').slice(0, -1);
};

export const PhantomLogsSection: FC = () => {
  const [logs, setLogs] = useState<PhantomLogEntry[]>(() =>
    getPhantomDebugLogs()
  );

  const handleRefresh = () => setLogs(getPhantomDebugLogs());

  const handleClear = () => {
    clearPhantomDebugLogs();
    setLogs([]);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <p className="text-xs text-gray-400">
          Logs from the Phantom redirect flow, persisted across tabs and page
          navigations.
        </p>

        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleClear}
          >
            Clear
          </Button>
        </div>
      </div>

      <div className="overflow-y-auto max-h-96 rounded border border-gray-200 bg-gray-50 p-2 font-mono text-[11px] leading-snug">
        {logs.length === 0 ? (
          <p className="text-gray-400 p-2">No logs yet.</p>
        ) : (
          logs.map((entry, i) => (
            <div
              key={i}
              className="py-1 border-b border-gray-100 last:border-0 break-all"
            >
              <div className="text-gray-400">
                {formatTimestamp(entry.timestamp)}
              </div>
              <div className="text-blue-700">{entry.message}</div>
              {entry.data !== undefined && (
                <div className="pl-2 text-gray-600">
                  {typeof entry.data === 'string'
                    ? entry.data
                    : JSON.stringify(entry.data)}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
